// frontend/src/components/AllocationTable.jsx
import React, { useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Skeleton,
} from '@mui/material';
import TableFooterSimple from '../common/TableFooterSimple';
import SearchTrigger from '../common/SearchTrigger';
import { formatMoney, formatPercent } from '../../utils/format';
import { SHADOWS } from '../../theme/tokens';

const eur = (n) => formatMoney(n);

const ROWS_PER_PAGE = 50;

export default function AllocationTable({ rows = [], onOpenSku, loading = false }) {

  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('alloc_total');

  const handleSort = (col) => {
    if (orderBy === col) setOrder(order === 'asc' ? 'desc' : 'asc');
    else {
      setOrderBy(col);
      setOrder('desc');
    }
  };

  const filtered = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    const base = q
      ? rows.filter((r) => {
          const hay = [r.sku, r.product_name, r.variant_name, r.vendor]
            .map((x) => (x || '').toString().toLowerCase())
            .join(' ');
          return hay.includes(q);
        })
      : rows;
    return base;
  }, [rows, query]);

  const enriched = useMemo(() => {
    return filtered.map((r) => {
      const qty = Number(r.qty || 0);
      const allocTotal = Number(r.alloc_total || 0);
      const revenue = Number(r.revenue_net || 0);
      return {
        ...r,
        per_unit: qty > 0 ? allocTotal / qty : 0,
        share_pct: revenue > 0 ? (allocTotal / revenue) * 100 : 0,
        margin_eur: revenue - allocTotal,
      };
    });
  }, [filtered]);

  const sorted = useMemo(() => {
    return [...enriched].sort((a, b) => {
      const va = a?.[orderBy] ?? 0;
      const vb = b?.[orderBy] ?? 0;
      if (vb < va) return order === 'asc' ? -1 : 1;
      if (vb > va) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [enriched, order, orderBy]);

  const paged = useMemo(() => {
    const start = page * ROWS_PER_PAGE;
    return sorted.slice(start, start + ROWS_PER_PAGE);
  }, [sorted, page]);

  const sums = useMemo(() => {
    return filtered.reduce(
      (acc, r) => {
        acc.qty += Number(r.qty || 0);
        acc.rev += Number(r.revenue_net || 0);
        acc.ful += Number(r.alloc_ful_cost || 0);
        acc.ship += Number(r.alloc_ship_cost || 0);
        acc.stor += Number(r.alloc_storage_cost || 0);
        acc.tot += Number(r.alloc_total || 0);
        return acc;
      },
      { qty: 0, rev: 0, ful: 0, ship: 0, stor: 0, tot: 0 }
    );
  }, [filtered]);

  return (
    <Box sx={{ width: '100%' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', md: 'center' }}
        sx={{ mb: 0.5 }}
      >
        <SearchTrigger
          value={query}
          onChange={(q) => { setQuery(q); setPage(0); }}
          placeholder="Search SKU, product, vendor…"
          sx={{ flex: 1 }}
        />
        <Typography variant="caption" color="text.secondary">
          Showing {paged.length} of {filtered.length} (filtered) / {rows.length} (total)
        </Typography>
      </Stack>

      <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Table
          size="small"
          sx={{
            borderCollapse: 'separate',
            borderSpacing: 0,
            '& thead th': {
              bgcolor: (theme) => theme.palette.grey[100],
              borderBottom: '1px solid',
              borderColor: 'divider',
              whiteSpace: 'nowrap',
              lineHeight: 1.4,
              fontSize: 12,
            },
            '& .MuiTableCell-root': {
              py: 1,
              px: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            },
            // Sticky first column (SKU)
            '& thead th:first-of-type, & tbody td:first-of-type': {
              position: 'sticky',
              left: 0,
              zIndex: 2,
              bgcolor: 'background.paper',
              boxShadow: SHADOWS.stickyColumn,
            },
            '& thead th:first-of-type': {
              zIndex: 3,
              bgcolor: (theme) => theme.palette.grey[100],
            },
          }}
        >
          <TableHead>
            <TableRow>
              {[
                { id: 'sku', label: 'SKU' },
                { id: 'product_name', label: 'Product' },
                { id: 'qty', label: 'Qty', numeric: true },
                { id: 'revenue_net', label: 'Revenue €', numeric: true },
                { id: 'alloc_ful_cost', label: 'Fulfillment €', numeric: true, hideOnMobile: true },
                { id: 'alloc_ship_cost', label: 'Shipping €', numeric: true, hideOnMobile: true },
                { id: 'alloc_storage_cost', label: 'Storage €', numeric: true, hideOnMobile: true },
                { id: 'alloc_total', label: 'Total €', numeric: true },
                { id: 'per_unit', label: 'Cost / unit €', numeric: true, hideOnMobile: true },
                { id: 'share_pct', label: 'Share %', numeric: true, hideOnMobile: true },
                { id: 'margin_eur', label: 'Margin €', numeric: true },
              ].map((col) => (
                <TableCell
                  key={col.id}
                  align={col.numeric ? 'right' : 'left'}
                  sortDirection={orderBy === col.id ? order : false}
                  sx={col.hideOnMobile ? { display: { xs: 'none', sm: 'table-cell' } } : undefined}
                >
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading
              ? Array.from({ length: ROWS_PER_PAGE }).map((_, i) => (
                  <TableRow key={`skel-${i}`}>
                    {[false, false, false, false, true, true, true, false, true, true, false].map((hideOnMobile, j) => (
                      <TableCell
                        key={j}
                        align={j >= 2 ? 'right' : 'left'}
                        sx={hideOnMobile ? { display: { xs: 'none', sm: 'table-cell' } } : undefined}
                      >
                        <Skeleton variant="text" width={j >= 2 ? 80 : 220} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : paged.map((r) => {
                  const name = r.product_name || r.variant_name || r.sku;
                  return (
                    <TableRow
                      key={r.sku}
                      hover
                      role="button"
                      tabIndex={0}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => onOpenSku?.(r.sku)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') onOpenSku?.(r.sku);
                      }}
                    >
                      <TableCell>{r.sku}</TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 340,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {name}
                        {r.vendor ? ` — ${r.vendor}` : ''}
                      </TableCell>
                      <TableCell align="right">{Math.round(r.qty || 0)}</TableCell>
                      <TableCell align="right">{eur(r.revenue_net)}</TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{eur(r.alloc_ful_cost)}</TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{eur(r.alloc_ship_cost)}</TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{eur(r.alloc_storage_cost)}</TableCell>
                      <TableCell align="right">
                        <strong>{eur(r.alloc_total)}</strong>
                      </TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{eur(r.per_unit)}</TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {formatPercent(Number(r.share_pct || 0) / 100, { digits: 1 })}
                      </TableCell>
                      <TableCell align="right">{eur(r.margin_eur)}</TableCell>
                    </TableRow>
                  );
                })}

            {!loading && sorted.length > 0 && (
              <TableRow>
                <TableCell colSpan={2}>
                  <strong>Totals (filtered)</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>{Math.round(sums.qty)}</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>{eur(sums.rev)}</strong>
                </TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <strong>{eur(sums.ful)}</strong>
                </TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <strong>{eur(sums.ship)}</strong>
                </TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <strong>{eur(sums.stor)}</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>{eur(sums.tot)}</strong>
                </TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <strong>{eur(sums.tot / Math.max(1, sums.qty))}</strong>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }} />
                <TableCell align="right">
                  <strong>{eur(sums.rev - sums.tot)}</strong>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <TableFooterSimple
        count={filtered.length}
        page={page}
        rowsPerPage={ROWS_PER_PAGE}
        onPageChange={(next) => setPage(next)}
        maxWidth="100%"
        leftSlot={`Ful ${eur(sums.ful)} • Ship ${eur(sums.ship)} • Stor ${eur(
          sums.stor
        )} • Total ${eur(sums.tot)}`}
      />
    </Box>
  );
}
